import React, { useState } from 'react';
import { Button, Card, Col, Modal, Row, Badge } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';

/**
 * Modal de solo visualización de un gasto rediseñado para máxima fidelidad estética
 * en modo claro y oscuro, respetando la estructura de dos columnas de la aplicación.
 */
const ModalVisualizarDetallesGasto = ({ show, selectedGasto, onClose }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();
    const [downloading, setDownloading] = useState(false);

    // Normalizar objeto de gasto
    const gasto = Array.isArray(selectedGasto) ? selectedGasto[0] : (selectedGasto || {});

    // Mapear campos redundantes entre listados y detalles individuales
    const descripcion = gasto.vDescripcion || gasto.vGastoDescripcion || "";
    const tipoGasto = gasto.vGastoTipoGasto || gasto.vTipoGasto || "Gasto General";
    const formaPago = gasto.vGastoFormaPago || gasto.vFormaPago || "No especificada";
    const responsable = gasto.vGastoResponsable || gasto.vUsuario || "No asignado";
    const cuentaBancaria = gasto.DetallesCuenta || gasto.vCuentaBancaria || "No especificada";
    const uuidFactura = gasto.vUUIDFactura || "";
    const total = gasto.dTotal || gasto.dGastoTotal || 0;
    const cantidad = gasto.dCantidad || gasto.dGastoCantidad || 0;
    const precioUnitario = gasto.dPrecioUnitario || gasto.dGastoPrecioUnitario || 0;
    const descuento = gasto.dDescuento || gasto.dGastoDescuento || 0;
    const importe = gasto.dImporte || gasto.dGastoImporte || 0;
    const iva = gasto.dIva || gasto.dGastoIva || 0;
    const isrRet = gasto.dIsrRet || gasto.dGastoIsrRet || 0;

    const formatDate = (value) => {
        try {
            if (!value) return "Sin fecha";
            const d = new Date(value);
            return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return value;
        }
    };

    const formatCurrency = (value) => {
        if (value == null || value === "") return "$0.00";
        try {
            const font = typeof value === 'number' ? value : parseFloat(value);
            if (Number.isNaN(font)) return "$0.00";
            return font.toLocaleString('es-MX', { style: 'currency', currency: gasto?.vMoneda || 'MXN' });
        } catch {
            return value;
        }
    };

    const descargarNotaDesdeApi = async () => {
        if (!gasto.idGasto) return;
        setDownloading(true);
        try {
            const response = await fetch(`/api/Gastos/ObtenerNotaVenta?idGasto=${gasto.idGasto}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    const { vNotaVenta_Base64, vNotaVenta_Name } = detalles.table2[0];

                    const byteCharacters = atob(vNotaVenta_Base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/octet-stream' });

                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = vNotaVenta_Name;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(link.href);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener nota de venta');
                }
            } else {
                showErrorToast('Error en la petición: ' + response.statusText);
            }
        } catch (error) {
            showErrorToast('Error al descargar nota de venta');
            console.error(error);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Modal 
            show={show} 
            centered 
            size="lg" 
            onHide={onClose} 
            backdrop="static" 
            keyboard={false}
            dialogClassName="modal-dialog-custom"
            contentClassName="border-0 shadow"
        >
            {/* Cabecera Estilo Premium */}
            <Modal.Header className="border-bottom px-4 py-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <div className="bg-soft-primary text-primary rounded p-2 d-inline-flex align-items-center justify-content-center">
                        <i className="fas fa-file-invoice-dollar fs-5" />
                    </div>
                    <h5 className="modal-title fw-bold text-900 mb-0">Detalles del Gasto</h5>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </Modal.Header>

            <Modal.Body className="p-4 bg-light">
                <Row className="g-4">
                    {/* Columna Izquierda: Descripción e Información */}
                    <Col lg={7}>
                        <div className="mb-4">
                            <label className="text-uppercase text-muted fw-bold fs--2 mb-2 d-block tracking-wider">
                                Descripción del Gasto
                            </label>
                            <div className="p-3 rounded-3 border bg-white shadow-sm position-relative overflow-hidden">
                                <p className="text-800 fw-medium mb-3 fs--1 lh-base" style={{ whiteSpace: 'pre-wrap' }}>
                                    {descripcion || "Sin descripción disponible"}
                                </p>
                                <div className="d-flex gap-2">
                                    <Badge bg="soft-primary" className="text-primary px-2 py-1 fs--2 fw-semibold">
                                        Activo
                                    </Badge>
                                    <Badge bg="soft-secondary" className="text-secondary px-2 py-1 fs--2 fw-semibold">
                                        Mobiliario
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <Row className="g-3 mb-4">
                            <Col sm={6}>
                                <div className="text-muted fs--2 fw-semibold text-uppercase mb-1">Tipo de Gasto</div>
                                <div className="text-900 fw-bold fs--1">{tipoGasto}</div>
                            </Col>
                            <Col sm={6}>
                                <div className="text-muted fs--2 fw-semibold text-uppercase mb-1">Forma de Pago</div>
                                <div className="text-900 fw-bold fs--1 d-flex align-items-center gap-2">
                                    <i className="fas fa-credit-card text-muted" /> {formaPago}
                                </div>
                            </Col>
                            <Col sm={6}>
                                <div className="text-muted fs--2 fw-semibold text-uppercase mb-1">Responsable</div>
                                <div className="text-900 fw-bold fs--1">{responsable}</div>
                            </Col>
                            <Col sm={6}>
                                <div className="text-muted fs--2 fw-semibold text-uppercase mb-1">Cuenta Bancaria</div>
                                <div className="text-900 fw-bold fs--1">{cuentaBancaria}</div>
                            </Col>
                        </Row>

                        {uuidFactura && (
                            <div className="mb-4">
                                <label className="text-uppercase text-muted fw-bold fs--2 mb-1 d-block tracking-wider">
                                    Factura Relacionada
                                </label>
                                <div className="text-900 fw-semibold fs--1 d-flex align-items-center gap-2">
                                    <i className="fas fa-receipt text-primary" />
                                    <span className="text-primary font-monospace">{uuidFactura}</span>
                                </div>
                            </div>
                        )}

                        <div className="d-flex flex-wrap gap-3 border-top pt-3 text-muted fs--2">
                            <div>
                                <i className="far fa-calendar-alt me-1" />
                                Fecha: <span className="fw-semibold text-700">{formatDate(gasto.dFechaGasto)}</span>
                            </div>
                            <div>
                                <i className="far fa-clock me-1" />
                                Última actualización: <span className="fw-semibold text-700">{formatDate(gasto.dFechaActualizacion || gasto.dUpdatedDate || gasto.dFechaMod)}</span>
                            </div>
                        </div>
                    </Col>

                    {/* Columna Derecha: Montos y Resumen */}
                    <Col lg={5}>
                        <Card className="border shadow-sm h-100 bg-white">
                            <Card.Body className="d-flex flex-column justify-content-between p-4">
                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                        <h6 className="text-uppercase text-muted fw-bold fs--2 mb-0 tracking-wider">
                                            Resumen de Montos
                                        </h6>
                                        <span className="badge bg-soft-secondary text-secondary fs--2 font-monospace">
                                            Moneda: {gasto?.vMoneda || 'MXN'}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed">
                                            <span className="text-700 fs--1">Cantidad</span>
                                            <span className="text-950 fw-bold fs--1">{cantidad}</span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed">
                                            <span className="text-700 fs--1">Precio unitario</span>
                                            <span className="text-950 fw-bold fs--1">{formatCurrency(precioUnitario)}</span>
                                        </div>
                                        {descuento > 0 && (
                                            <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed text-danger">
                                                <span className="fs--1">Descuento</span>
                                                <span className="fw-bold fs--1">-{formatCurrency(descuento)}</span>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed">
                                            <span className="text-700 fs--1">Subtotal (Importe)</span>
                                            <span className="text-950 fw-bold fs--1">{formatCurrency(importe)}</span>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed">
                                            <span className="text-700 fs--1">IVA (16%)</span>
                                            <span className="text-950 fw-bold fs--1">{formatCurrency(iva)}</span>
                                        </div>
                                        {gasto.dIsrRet > 0 && (
                                            <div className="d-flex align-items-center justify-content-between py-2 border-bottom border-dashed text-warning">
                                                <span className="fs--1">ISR Retenido</span>
                                                <span className="fw-bold fs--1">-{formatCurrency(isrRet)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Sección de Total Limpia */}
                                <div className="mt-4 p-3 rounded-3 bg-light border">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className="text-uppercase fs--2 fw-bold tracking-wider text-700">
                                            Total
                                        </span>
                                        <Badge bg="soft-success" className="text-success fs--2 fw-semibold px-2 py-1">
                                            Liquidado
                                        </Badge>
                                    </div>
                                    <h2 className="mb-0 fw-bold text-900 display-6">
                                        {formatCurrency(total)}
                                    </h2>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className="border-top px-4 py-3 d-flex justify-content-end gap-2 bg-white rounded-bottom-3">
                {gasto.vNotaVenta_Name && (
                    <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        disabled={downloading}
                        onClick={descargarNotaDesdeApi}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`} />
                        Descargar Nota
                    </Button>
                )}
                <Button variant="primary" size="sm" onClick={onClose}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalVisualizarDetallesGasto.propTypes = {
    show: PropTypes.bool.isRequired,
    selectedGasto: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    onClose: PropTypes.func.isRequired
};

export default ModalVisualizarDetallesGasto;
